import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserInfo } from '../../api/auth';
import { NavigationDrawer, AuthenticatedUser } from './NavigationDrawer';
import { Button } from '../ui/button';
import { logLocation } from '../../api/logs';

const LOCATION_CAPTURE_INTERVAL_MS = 15 * 60 * 1000;

interface ProtectedLayoutRenderProps {
  user: AuthenticatedUser;
}

interface ProtectedLayoutProps {
  children: React.ReactNode | ((args: ProtectedLayoutRenderProps) => React.ReactNode);
}

export const ProtectedLayout: React.FC<ProtectedLayoutProps> = ({ children }) => {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      navigate('/login');
      return;
    }

    const loadUser = async () => {
      try {
        const data = await getUserInfo(accessToken);
        setUser(data);
      } catch {
        setError('Failed to fetch user info. Please log in again.');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    navigate('/login');
  };

  useEffect(() => {
    if (!user) {
      return;
    }

    const accessToken = localStorage.getItem('access_token');
    if (!accessToken || !navigator.geolocation) {
      return;
    }

    const tokenSessionId = accessToken.slice(-16);
    const sessionKey = `geo_logged_session_${user.id}_${tokenSessionId}`;
    const inFlightKey = `geo_capture_in_flight_${user.id}_${tokenSessionId}`;
    const lastCapturedAtKey = `geo_last_captured_at_${user.id}_${tokenSessionId}`;
    const promptKey = `geo_prompted_once_${user.id}`;

    const runCapture = async (reason: 'login' | 'periodic', force: boolean) => {
      if (sessionStorage.getItem(inFlightKey) === '1') {
        return;
      }

      const lastCapturedAtRaw = sessionStorage.getItem(lastCapturedAtKey);
      const lastCapturedAt = lastCapturedAtRaw ? Number(lastCapturedAtRaw) : 0;
      if (!force && lastCapturedAt > 0 && Date.now() - lastCapturedAt < LOCATION_CAPTURE_INTERVAL_MS) {
        return;
      }

      sessionStorage.setItem(inFlightKey, '1');
      let permissionState: PermissionState | 'unknown' = 'unknown';

      try {
        if ('permissions' in navigator && navigator.permissions?.query) {
          const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
          permissionState = status.state;
        }
      } catch {
        permissionState = 'unknown';
      }

      if (permissionState === 'denied') {
        sessionStorage.removeItem(inFlightKey);
        return;
      }

      if (permissionState === 'prompt' && localStorage.getItem(promptKey) === '1') {
        sessionStorage.removeItem(inFlightKey);
        return;
      }

      if (permissionState === 'prompt') {
        localStorage.setItem(promptKey, '1');
      }

      navigator.geolocation.getCurrentPosition(
        async position => {
          try {
            await logLocation(accessToken, {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy_meters: position.coords.accuracy,
              reason,
            });
            sessionStorage.setItem(lastCapturedAtKey, String(Date.now()));
          } catch {
            // Best-effort telemetry; do not block app usage.
          } finally {
            sessionStorage.removeItem(inFlightKey);
          }
        },
        () => {
          sessionStorage.removeItem(inFlightKey);
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    };

    if (sessionStorage.getItem(sessionKey) !== '1') {
      // First capture once per browser tab session right after login.
      sessionStorage.setItem(sessionKey, '1');
      void runCapture('login', true);
    }

    const intervalId = window.setInterval(() => {
      void runCapture('periodic', false);
    }, LOCATION_CAPTURE_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [user]);

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background text-foreground">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-muted border-t-primary" />
        <p className="text-sm text-muted-foreground">Loading your workspace…</p>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background text-foreground">
        <p className="text-lg font-medium">{error || 'Unable to continue.'}</p>
        <Button onClick={handleLogout}>Return to Login</Button>
      </div>
    );
  }

  const content =
    typeof children === 'function' ? (children as (args: ProtectedLayoutRenderProps) => React.ReactNode)({ user }) : children;

  return (
    <NavigationDrawer user={user} onLogout={handleLogout}>
      {content}
    </NavigationDrawer>
  );
};
